import { useState, useEffect, useCallback } from "react";
import {
  Activity, Database, Cpu, HardDrive, Clock, AlertTriangle,
  CheckCircle2, XCircle, RefreshCw, Wifi, Server,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function authFetch(url) {
  const token = localStorage.getItem("token");
  return fetch(url, { headers: { Authorization: `Bearer ${token}` } });
}

function StatusDot({ ok }) {
  return (
    <span className={`inline-block w-2 h-2 rounded-full ${ok ? "bg-emerald-400" : "bg-red-400"}`} />
  );
}

function MetricCard({ icon: Icon, label, value, sub, color = "var(--tga-text-primary)", warn }) {
  return (
    <div className="rounded-lg p-3 border" style={{ backgroundColor: "var(--tga-bg-card)", borderColor: warn ? "rgba(239,68,68,0.4)" : "var(--tga-border)" }}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="w-3.5 h-3.5" style={{ color: "var(--tga-text-dim)" }} />
        <span className="text-[10px] font-medium uppercase tracking-wider" style={{ color: "var(--tga-text-dim)" }}>{label}</span>
      </div>
      <p className="text-lg font-bold" style={{ color }}>{value}</p>
      {sub && <p className="text-[10px]" style={{ color: "var(--tga-text-dim)" }}>{sub}</p>}
    </div>
  );
}

export default function ServerStatus() {
  const [data, setData] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pingMs, setPingMs] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);

    // Health check (no auth needed, simpler)
    try {
      const t0 = performance.now();
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), 10000);
      const hRes = await fetch(`${API}/health`, { signal: ctrl.signal });
      clearTimeout(timer);
      setPingMs(Math.round(performance.now() - t0));
      if (hRes.ok) setHealth(await hRes.json());
    } catch {
      setPingMs(-1);
    }

    // Diagnostics (requires admin auth)
    try {
      const token = localStorage.getItem("token");
      if (token) {
        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), 12000);
        const res = await fetch(`${API}/diagnostics`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: ctrl.signal,
        });
        clearTimeout(timer);
        if (res.ok) setData(await res.json());
      }
    } catch {}

    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 30s
  useEffect(() => {
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, [load]);

  const dbOk = health?.db === true || data?.database?.connected === true;
  const serverOk = health?.status === "ok";
  const showLoading = loading && !health;

  return (
    <div className="space-y-4" data-testid="server-status">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Server className="w-4 h-4" style={{ color: "var(--tga-blue)" }} />
          <span className="text-[14px] font-semibold" style={{ color: "var(--tga-text-bright)" }}>Server-Status</span>
          {showLoading ? (
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-500/15 text-amber-400">
              <RefreshCw className="w-3 h-3 animate-spin" /> Laden...
            </span>
          ) : serverOk ? (
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-500/15 text-emerald-400">
              <CheckCircle2 className="w-3 h-3" /> Online
            </span>
          ) : (
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-red-500/15 text-red-400">
              <XCircle className="w-3 h-3" /> Problem
            </span>
          )}
        </div>
        <button onClick={load} disabled={loading}
          className="flex items-center gap-1 px-2.5 py-1 rounded-lg border text-[11px] transition-colors"
          style={{ borderColor: "var(--tga-border-strong)", color: "var(--tga-text-secondary)" }}
          data-testid="refresh-status-btn">
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Aktualisieren
        </button>
      </div>

      {/* Quick Status Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <MetricCard icon={Wifi} label="Ping" value={pingMs > 0 ? `${pingMs}ms` : "---"} color={pingMs > 2000 ? "rgb(239,68,68)" : pingMs > 500 ? "rgb(234,179,8)" : "rgb(16,185,129)"} warn={pingMs > 2000} sub={pingMs > 2000 ? "Sehr langsam!" : pingMs > 500 ? "Langsam" : "Normal"} />
        <MetricCard icon={Database} label="Datenbank" value={showLoading ? "Pruefen..." : dbOk ? "OK" : "Fehler"} color={showLoading ? "rgb(234,179,8)" : dbOk ? "rgb(16,185,129)" : "rgb(239,68,68)"} warn={!showLoading && !dbOk} sub={data?.database?.latency_ms ? `${data.database.latency_ms}ms Latenz` : ""} />
        <MetricCard icon={Clock} label="Uptime" value={data?.server?.uptime_human || "---"} sub={data?.server?.started_at ? `Start: ${new Date(data.server.started_at).toLocaleString("de-DE")}` : ""} />
        <MetricCard icon={Activity} label="Requests" value={data?.requests?.total?.toLocaleString() || "0"} sub={`${data?.requests?.errors || 0} Fehler, ${data?.requests?.slow || 0} langsam`} warn={(data?.requests?.errors || 0) > 10} />
      </div>

      {/* System Resources */}
      {data?.system && !data.system.error && (
        <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: "var(--tga-bg-card)", borderColor: "var(--tga-border)" }}>
          <div className="px-4 py-2" style={{ borderBottom: "1px solid var(--tga-border)" }}>
            <span className="text-[12px] font-semibold" style={{ color: "var(--tga-text-muted)" }}>System-Ressourcen</span>
          </div>
          <div className="grid grid-cols-3 gap-3 p-4">
            <div>
              <div className="flex items-center gap-1.5 mb-1">
                <Cpu className="w-3.5 h-3.5" style={{ color: "var(--tga-text-dim)" }} />
                <span className="text-[11px]" style={{ color: "var(--tga-text-muted)" }}>CPU</span>
              </div>
              <div className="w-full h-2 rounded-full overflow-hidden" style={{ backgroundColor: "var(--tga-bg-elevated)" }}>
                <div className="h-full rounded-full transition-all" style={{ width: `${data.system.cpu_percent}%`, backgroundColor: data.system.cpu_percent > 80 ? "rgb(239,68,68)" : data.system.cpu_percent > 50 ? "rgb(234,179,8)" : "rgb(16,185,129)" }} />
              </div>
              <span className="text-[11px] font-mono" style={{ color: "var(--tga-text-primary)" }}>{data.system.cpu_percent}%</span>
            </div>
            <div>
              <div className="flex items-center gap-1.5 mb-1">
                <Activity className="w-3.5 h-3.5" style={{ color: "var(--tga-text-dim)" }} />
                <span className="text-[11px]" style={{ color: "var(--tga-text-muted)" }}>RAM</span>
              </div>
              <div className="w-full h-2 rounded-full overflow-hidden" style={{ backgroundColor: "var(--tga-bg-elevated)" }}>
                <div className="h-full rounded-full transition-all" style={{ width: `${data.system.memory_percent}%`, backgroundColor: data.system.memory_percent > 90 ? "rgb(239,68,68)" : data.system.memory_percent > 70 ? "rgb(234,179,8)" : "rgb(16,185,129)" }} />
              </div>
              <span className="text-[11px] font-mono" style={{ color: "var(--tga-text-primary)" }}>{data.system.memory_used_mb}/{data.system.memory_total_mb} MB ({data.system.memory_percent}%)</span>
            </div>
            <div>
              <div className="flex items-center gap-1.5 mb-1">
                <HardDrive className="w-3.5 h-3.5" style={{ color: "var(--tga-text-dim)" }} />
                <span className="text-[11px]" style={{ color: "var(--tga-text-muted)" }}>Disk</span>
              </div>
              <div className="w-full h-2 rounded-full overflow-hidden" style={{ backgroundColor: "var(--tga-bg-elevated)" }}>
                <div className="h-full rounded-full transition-all" style={{ width: `${data.system.disk_used_percent}%`, backgroundColor: data.system.disk_used_percent > 90 ? "rgb(239,68,68)" : "rgb(16,185,129)" }} />
              </div>
              <span className="text-[11px] font-mono" style={{ color: "var(--tga-text-primary)" }}>{data.system.disk_total_gb} GB ({data.system.disk_used_percent}%)</span>
            </div>
          </div>
        </div>
      )}

      {/* DB Connection Pool */}
      {data?.database && (
        <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: "var(--tga-bg-card)", borderColor: "var(--tga-border)" }}>
          <div className="px-4 py-2" style={{ borderBottom: "1px solid var(--tga-border)" }}>
            <span className="text-[12px] font-semibold" style={{ color: "var(--tga-text-muted)" }}>Datenbank</span>
          </div>
          <div className="px-4 py-3 flex flex-wrap gap-4 text-[12px]">
            <div>
              <StatusDot ok={data.database.connected} />
              <span className="ml-1.5" style={{ color: "var(--tga-text-primary)" }}>
                {data.database.connected ? "Verbunden" : "Getrennt"}
              </span>
            </div>
            {data.database.latency_ms !== undefined && (
              <div style={{ color: "var(--tga-text-secondary)" }}>Latenz: <span className="font-mono font-semibold">{data.database.latency_ms}ms</span></div>
            )}
            {data.database.max_pool_size && (
              <div style={{ color: "var(--tga-text-secondary)" }}>Pool: {data.database.min_pool_size}-{data.database.max_pool_size}</div>
            )}
            {data.database.error && (
              <div className="text-red-400 text-[11px]">{data.database.error}</div>
            )}
          </div>
        </div>
      )}

      {/* Slow Requests Log */}
      {data?.slow_requests?.length > 0 && (
        <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: "var(--tga-bg-card)", borderColor: "var(--tga-border)" }}>
          <div className="px-4 py-2 flex items-center gap-2" style={{ borderBottom: "1px solid var(--tga-border)" }}>
            <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
            <span className="text-[12px] font-semibold" style={{ color: "var(--tga-text-muted)" }}>Langsame Requests (letzten {data.slow_requests.length})</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead>
                <tr style={{ backgroundColor: "var(--tga-bg-elevated)" }}>
                  <th className="text-left px-3 py-1.5 font-medium" style={{ color: "var(--tga-text-dim)" }}>Zeit</th>
                  <th className="text-left px-3 py-1.5 font-medium" style={{ color: "var(--tga-text-dim)" }}>Methode</th>
                  <th className="text-left px-3 py-1.5 font-medium" style={{ color: "var(--tga-text-dim)" }}>Pfad</th>
                  <th className="text-right px-3 py-1.5 font-medium" style={{ color: "var(--tga-text-dim)" }}>Dauer</th>
                  <th className="text-center px-3 py-1.5 font-medium" style={{ color: "var(--tga-text-dim)" }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {data.slow_requests.slice().reverse().map((r, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid var(--tga-border)" }}>
                    <td className="px-3 py-1.5 font-mono" style={{ color: "var(--tga-text-secondary)" }}>{new Date(r.ts).toLocaleTimeString("de-DE")}</td>
                    <td className="px-3 py-1.5"><span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${r.method === "POST" ? "bg-blue-500/15 text-blue-400" : "bg-emerald-500/15 text-emerald-400"}`}>{r.method}</span></td>
                    <td className="px-3 py-1.5 font-mono" style={{ color: "var(--tga-text-primary)" }}>{r.path}</td>
                    <td className="px-3 py-1.5 text-right font-mono font-semibold" style={{ color: r.duration_ms > 5000 ? "rgb(239,68,68)" : "rgb(234,179,8)" }}>{r.duration_ms}ms</td>
                    <td className="px-3 py-1.5 text-center"><span className={`text-[10px] font-mono ${r.status >= 500 ? "text-red-400" : "text-emerald-400"}`}>{r.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Strato Tipps */}
      <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: "var(--tga-bg-card)", borderColor: "var(--tga-border)" }}>
        <div className="px-4 py-2" style={{ borderBottom: "1px solid var(--tga-border)" }}>
          <span className="text-[12px] font-semibold" style={{ color: "var(--tga-text-muted)" }}>Strato Server-Checkliste</span>
        </div>
        <div className="px-4 py-3 space-y-2 text-[11px]" style={{ color: "var(--tga-text-secondary)" }}>
          <p><strong>1. Backend-Prozess</strong>: Laeuft uvicorn/gunicorn? → <code className="px-1 py-0.5 rounded" style={{ backgroundColor: "var(--tga-bg-elevated)" }}>ps aux | grep uvicorn</code></p>
          <p><strong>2. NGINX Timeout</strong>: Zu kurz? → <code className="px-1 py-0.5 rounded" style={{ backgroundColor: "var(--tga-bg-elevated)" }}>proxy_read_timeout 120s;</code> in nginx.conf</p>
          <p><strong>3. Workers</strong>: Zu wenige? → <code className="px-1 py-0.5 rounded" style={{ backgroundColor: "var(--tga-bg-elevated)" }}>gunicorn -w 4 -k uvicorn.workers.UvicornWorker</code></p>
          <p><strong>4. Memory</strong>: Server voll? → Wenn RAM &gt; 90%, mehr Speicher buchen</p>
          <p><strong>5. DB</strong>: Latenz hoch? → Wenn &gt; 100ms, Index pruefen</p>
        </div>
      </div>
    </div>
  );
}
